import { formatDate } from "@plasma/utils/time";
import { type ColumnDef, createColumnHelper } from "@tanstack/react-table";
import { useQuery } from "convex/react";
import { useMemo } from "react";

import {
  locationSalesGetOrdersByTenderType,
  type TenderTypeEntry,
} from "~/api-refs";
import { SalesDataTable } from "~/components/reports/sales-data-table";
import { LoadingPanel } from "~/components/shared/loading-panel";
import { toConvexTableId } from "~/lib/convex-table-id";

type ExtendedBill = TenderTypeEntry;

interface PaymentsViewProps {
  locationId: string;
  dateRange: {
    from: Date;
    to: Date;
  };
  tableId: string;
}

export const PaymentsView = ({
  locationId,
  dateRange,
  tableId,
}: PaymentsViewProps) => {
  const data =
    useQuery(locationSalesGetOrdersByTenderType, {
      dateRange: {
        from: dateRange.from.getTime(),
        to: dateRange.to.getTime(),
      },
      locationId: toConvexTableId("locations", locationId),
    }) ?? [];
  const isLoading = data === undefined;

  const columnHelper = createColumnHelper<ExtendedBill>();

  const columns = useMemo(
    () =>
      [
        columnHelper.accessor("posDevice", {
          header: "POS Device",
          cell: ({ getValue }) => getValue()?.name ?? "N/A",
          filterFn: ({ original }, _, filterValue: string[]) =>
            original.posDevice
              ? filterValue.includes(original.posDevice.id)
              : false,
          meta: {
            getCellForExport: (e: ExtendedBill["posDevice"]) =>
              e?.name ?? "N/A",
            faceting: {
              title: "POS Device",
              getOption: (e: ExtendedBill["posDevice"]) => ({
                value: e?.id ?? "",
                label: e?.name ?? e?.id ?? "N/A",
              }),
            },
          },
        }),
        columnHelper.accessor("orderCreatedAt", {
          header: "Date of Trans.",
          cell: ({ getValue }) => formatDate(getValue(), "MMM D, YYYY"),
          meta: {
            getCellForExport: (d: ExtendedBill["orderCreatedAt"]) =>
              d.toISOString(),
          },
        }),
        columnHelper.accessor("billId", {
          header: "Bill ID",
        }),
        columnHelper.accessor("orderId", {
          header: "Order ID",
        }),
        columnHelper.accessor("employee", {
          header: "Employee Name",
          cell: ({ getValue }) => {
            const employee = getValue();
            if (!employee) {
              return "N/A";
            }
            return (
              <div className="flex items-center gap-3">
                <img
                  alt={`${employee.firstName} ${employee.lastName}'s avatar`}
                  className="size-6 rounded-full"
                  height={24}
                  src={employee.imageUrl ?? "/images/avatar-placeholder.svg"}
                  width={24}
                />
                <p>
                  {employee.firstName} {employee.lastName}
                </p>
              </div>
            );
          },
          filterFn: ({ original }, _, filterValue: string[]) =>
            original.employee
              ? filterValue.includes(original.employee.id)
              : false,
          meta: {
            getCellForExport: (e: ExtendedBill["employee"]) =>
              e ? `${e.firstName} ${e.lastName}` : "N/A",
            faceting: {
              title: "Employee",
              getOption: (e: ExtendedBill["employee"]) => ({
                value: e?.id ?? "",
                label: e ? `${e.firstName} ${e.lastName}` : "N/A",
              }),
            },
          },
        }),
        columnHelper.accessor("total", {
          header: "Bill Total",
          cell: ({ getValue }) =>
            getValue()?.toLocaleString("en-US", {
              style: "currency",
              currency: "USD",
            }),
          meta: {
            getCellForExport: (d: ExtendedBill["total"]) => d,
          },
        }),
        columnHelper.accessor("tenderTypes", {
          header: "Tender Type",
          cell: ({ getValue }) => {
            const types = getValue();
            return types.length > 0 ? types.join(", ") : "N/A";
          },
          filterFn: ({ original }, _, filterValue: string[]) =>
            original.tenderTypes.some((type) => filterValue.includes(type)),
          enableColumnFilter: false,
          meta: {
            getCellForExport: (types: ExtendedBill["tenderTypes"]) =>
              types.length > 0 ? types.join(", ") : "N/A",
          },
        }),
        columnHelper.accessor("tipAmount", {
          header: "Tip Amount",
          cell: ({ getValue }) =>
            getValue()?.toLocaleString("en-US", {
              style: "currency",
              currency: "USD",
            }) ?? "-",
          meta: {
            getCellForExport: (d: ExtendedBill["tipAmount"]) => d,
          },
        }),
      ] as ColumnDef<ExtendedBill>[],
    [columnHelper]
  );

  if (isLoading) {
    return <LoadingPanel message="Loading payment data..." />;
  }

  return <SalesDataTable columns={columns} data={data} id={tableId} />;
};
